import { ArrowLeft } from "lucide-react"
import { useNavigate, useParams } from "react-router-dom"
import { useSWRConfig } from "swr"

import { AlfredDeviceCreateView } from "@/components/alfred/alfred-device-config-view"
import { DashboardHeader } from "@/components/shared/dashboard-header"
import { ApiErrorState, ClientNotFoundState } from "@/components/shared/empty-states"
import { DevicesTableSkeleton } from "@/components/shared/loading-skeletons"
import { Button } from "@/components/ui/button"
import { useAlfredClients } from "@/hooks/use-alfred-clients"
import { useAlfredGlobalConfig } from "@/hooks/use-alfred-config"
import { useCreateAlfredDevice } from "@/hooks/use-alfred-devices"
import { useToast } from "@/hooks/use-toast"
import type { CreateAlfredDeviceRequest } from "@/types/alfred-device"

export function AlfredDeviceCreatePage() {
  const navigate = useNavigate()
  const { id: clientPrimaryKey } = useParams<{ id?: string }>()
  const { toast } = useToast()
  const { mutate } = useSWRConfig()
  const { data: clients, error: clientsError, isLoading: clientsLoading, mutate: reloadClients } = useAlfredClients()
  const {
    data: globalConfig,
    error: globalError,
    isLoading: globalLoading,
    mutate: reloadGlobal,
  } = useAlfredGlobalConfig()
  const createDevice = useCreateAlfredDevice()

  const isScopedCreate = clientPrimaryKey !== undefined
  const numericClientPrimaryKey = isScopedCreate ? Number(clientPrimaryKey) : undefined
  const client = numericClientPrimaryKey !== undefined && Number.isInteger(numericClientPrimaryKey)
    ? clients?.find((candidate) => candidate.id === numericClientPrimaryKey)
    : undefined
  const backPath = isScopedCreate
    ? `/alfred/clients/${clientPrimaryKey}/devices`
    : "/alfred/devices"
  const backLabel = client ? `Back to ${client.clientId} devices` : "Back to Devices"

  const handleCreate = async (request: CreateAlfredDeviceRequest) => {
    try {
      await createDevice.trigger(request)
      await mutate(
        (key) =>
          Array.isArray(key) &&
          key[0] === "alfred" &&
          (key[1] === "devices" || (key[1] === "clients" && key[3] === "devices")),
      )
      toast({
        variant: "success",
        title: "Device created successfully",
        description: `Device ${request.deviceId} has been added.`,
      })
      navigate(backPath)
    } catch (error) {
      toast({
        variant: "error",
        title: "Failed to create device",
        description: error instanceof Error ? error.message : "Please try again.",
      })
    }
  }

  if (isScopedCreate && clients && !clientsError && !client) {
    return (
      <>
        <DashboardHeader />
        <main className="container mx-auto px-4 py-6">
          <ClientNotFoundState onAction={() => navigate("/alfred/clients")} />
        </main>
      </>
    )
  }

  return (
    <>
      <DashboardHeader />
      <main className="container mx-auto px-4 py-6 space-y-6">
        <div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(backPath)}
            className="text-muted-foreground hover:text-foreground hover:bg-secondary -ml-2 mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            {backLabel}
          </Button>
          <h1 className="text-2xl font-semibold text-foreground">Add device</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Register a new Alfred device. Fields left on their defaults inherit the global configuration.
          </p>
        </div>

        {clientsLoading || globalLoading ? <DevicesTableSkeleton /> : null}
        {clientsError || globalError ? (
          <ApiErrorState onRetry={() => void Promise.all([reloadClients(), reloadGlobal()])} />
        ) : null}
        {clients && globalConfig ? (
          <AlfredDeviceCreateView
            clients={clients}
            globalConfig={globalConfig}
            defaultClientId={client?.clientId}
            onCreate={handleCreate}
            isCreating={createDevice.isMutating}
          />
        ) : null}
      </main>
    </>
  )
}
